import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellRing, Volume2, VolumeX, X, Check, CheckCheck, TrendingUp, Trophy, Gift, Target, Wallet, CheckCircle2, XCircle, MessageCircle, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import NotificationPanelWinsTicker from './NotificationPanelWinsTicker';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const STORAGE_KEY = 'notif_sound_muted';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

const typeIcons = {
    bet_won: <TrendingUp className="w-4 h-4 text-[#39FF14]" />,
    bet_lost: <TrendingUp className="w-4 h-4 text-[#FF3B30]" />,
    bet_void: <TrendingUp className="w-4 h-4 text-[#A0AAB2]" />,
    tournament: <Trophy className="w-4 h-4 text-[#FFD700]" />,
    pickem: <Target className="w-4 h-4 text-[#00FFFF]" />,
    bonus: <Gift className="w-4 h-4 text-[#FF007F]" />,
    cashout_paid: <Wallet className="w-4 h-4 text-[#39FF14]" />,
    cashout_approved: <CheckCircle2 className="w-4 h-4 text-[#FFD700]" />,
    cashout_rejected: <XCircle className="w-4 h-4 text-[#FF3B30]" />,
    slot_big_win: <Trophy className="w-4 h-4 text-[#FFD700]" />,
    admin_message: <MessageCircle className="w-4 h-4 text-[#FFD700]" />,
    info: <Bell className="w-4 h-4 text-[#A0AAB2]" />,
};

function timeAgo(iso) {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

const NotificationBell = () => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [ring, setRing] = useState(false);
    const [muted, setMuted] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
    });
    const panelRef = useRef(null);
    const contentRef = useRef(null);
    const seenIdsRef = useRef(null); // null = not yet initialized (first fetch baselines)
    const audioCtxRef = useRef(null);

    const playDing = useCallback(() => {
        if (muted) return;
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
            const ctx = audioCtxRef.current;
            if (ctx.state === 'suspended') ctx.resume();
            const now = ctx.currentTime;
            // Two-tone ding: 880Hz -> 1320Hz
            const tones = [
                { f: 880, t: now,        dur: 0.18 },
                { f: 1320, t: now + 0.10, dur: 0.22 },
            ];
            tones.forEach(({ f, t, dur }) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = f;
                gain.gain.setValueAtTime(0.0001, t);
                gain.gain.exponentialRampToValueAtTime(0.22, t + 0.01);
                gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(t);
                osc.stop(t + dur + 0.02);
            });
        } catch {}
    }, [muted]);

    const showNotifToast = useCallback((n) => {
        const icon = typeIcons[n.type] || typeIcons.info;
        toast.custom((id) => (
            <div
                onClick={() => { toast.dismiss(id); setOpen(true); }}
                className="cursor-pointer flex items-start gap-3 bg-[#121212] border border-[#FFD700]/30 rounded-xl shadow-2xl p-3 pr-4 min-w-[280px] max-w-[360px] hover:border-[#FFD700]/60 transition-colors"
                data-testid="notification-toast"
            >
                <div className="mt-0.5 shrink-0">{icon}</div>
                <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{n.title}</p>
                    <p className="text-[#A0AAB2] text-xs mt-0.5 line-clamp-2">{n.message}</p>
                </div>
                <button
                    onClick={(e) => { e.stopPropagation(); toast.dismiss(id); }}
                    className="text-[#A0AAB2] hover:text-white shrink-0 mt-0.5"
                    aria-label="Dismiss"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        ), { duration: 6000 });
    }, []);

    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await axios.get(`${API}/notifications?limit=20`, { withCredentials: true });
            const list = data.notifications || [];
            setNotifications(list);
            setUnreadCount(data.unread_count || 0);

            // Detect new notifications since last poll
            const currentIds = new Set(list.map(n => n.id));
            if (seenIdsRef.current === null) {
                // First fetch: baseline only, no alerts
                seenIdsRef.current = currentIds;
            } else {
                const fresh = list.filter(n => !seenIdsRef.current.has(n.id) && !n.read);
                if (fresh.length > 0) {
                    setRing(true);
                    setTimeout(() => setRing(false), 1600);
                    playDing();
                    // Show toast for up to 3 newest fresh items
                    fresh.slice(0, 3).forEach((n, i) => {
                        setTimeout(() => showNotifToast(n), i * 250);
                    });
                }
                seenIdsRef.current = currentIds;
            }
        } catch {}
    }, [user, playDing, showNotifToast]);

    const toggleMuted = (e) => {
        e.stopPropagation();
        setMuted(m => {
            const next = !m;
            try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch {}
            return next;
        });
    };

    // Poll notifications every 15s for near real-time feel
    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 15000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    // Check push subscription status
    useEffect(() => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            navigator.serviceWorker.ready.then((reg) => {
                reg.pushManager.getSubscription().then((sub) => {
                    setPushEnabled(!!sub);
                });
            });
        }
    }, []);

    // Close panel on outside click
    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target) &&
                contentRef.current && !contentRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const markAllRead = async () => {
        try {
            await axios.post(`${API}/notifications/mark-read`, { mark_all: true }, { withCredentials: true });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch {}
    };

    const subscribePush = async () => {
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
            });
            const subJson = sub.toJSON();
            await axios.post(`${API}/notifications/subscribe`, {
                endpoint: subJson.endpoint,
                keys: subJson.keys,
            }, { withCredentials: true });
            setPushEnabled(true);
        } catch (e) {
            console.error('Push subscription failed:', e);
        }
    };

    if (!user) return null;

    return (
        <div className="relative" ref={panelRef}>
            <button
                onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
                className={`relative p-2 transition-colors ${ring ? 'text-[#FFD700] notif-bell-ring' : 'text-[#A0AAB2] hover:text-white'}`}
                data-testid="notification-bell"
                aria-label="Notifications"
            >
                {ring ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
                {unreadCount > 0 && (
                    <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#FF3B30] text-white text-[10px] font-bold px-1 ${ring ? 'notif-badge-pulse' : ''}`} data-testid="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {createPortal(
                <AnimatePresence>
                    {open && (
                        <>
                        {/* Mobile: full-screen overlay */}
                        <motion.div
                            key="notif-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/70 z-[9998]"
                            onClick={() => setOpen(false)}
                        />
                        <motion.div
                            key="notif-panel"
                            ref={contentRef}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-x-0 bottom-0 top-0 z-[9999] lg:inset-auto lg:right-4 lg:top-16 lg:w-96 lg:max-h-[70vh] lg:rounded-xl bg-[#121212] lg:border border-white/10 shadow-2xl flex flex-col"
                            data-testid="notification-panel"
                        >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
                            <h3 className="text-white font-bold text-base">Notifications</h3>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={toggleMuted}
                                    className="text-[#A0AAB2] hover:text-white p-1"
                                    aria-label={muted ? 'Unmute notification sounds' : 'Mute notification sounds'}
                                    title={muted ? 'Sounds off' : 'Sounds on'}
                                    data-testid="notification-sound-toggle"
                                >
                                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                                </button>
                                {unreadCount > 0 && (
                                    <button onClick={markAllRead} className="text-xs text-[#39FF14] hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
                                        <CheckCheck className="w-3 h-3" /> Mark all read
                                    </button>
                                )}
                                <button onClick={() => setOpen(false)} className="text-[#A0AAB2] hover:text-white p-1" data-testid="close-notifications-btn">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Casino-floor "live wins" buzz ticker */}
                        <NotificationPanelWinsTicker />

                        {/* Push notification prompt — copy adapts to staff vs player. */}
                        {!pushEnabled && 'PushManager' in window && (
                            <div className="px-4 py-3 border-b border-white/10 bg-[#FFD700]/5 shrink-0">
                                <p className="text-xs text-[#A0AAB2] mb-2">
                                    {user?.role === 'admin' || user?.role === 'cashier'
                                        ? 'Get pinged when new players sign up or request cashouts — even when the app is closed.'
                                        : 'Get notified when bets settle, contests open, and more'}
                                </p>
                                <Button size="sm" onClick={subscribePush} className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 text-xs h-7" data-testid="enable-push-btn">
                                    Enable Push Notifications
                                </Button>
                            </div>
                        )}

                        {/* Notification list */}
                        <div className="overflow-y-auto flex-1 min-h-0" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                            {notifications.length === 0 ? (
                                <div className="py-12 text-center">
                                    <Bell className="w-8 h-8 text-[#A0AAB2] mx-auto mb-2 opacity-40" />
                                    <p className="text-[#A0AAB2] text-sm">No notifications yet</p>
                                </div>
                            ) : (
                                notifications.map((n) => (
                                    <NotificationRow key={n.id} n={n} />
                                ))
                            )}
                        </div>
                    </motion.div>
                    </>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default NotificationBell;

// Renders a single notification + inline reply composer for admin_message type
const NotificationRow = ({ n }) => {
    const [replyOpen, setReplyOpen] = useState(false);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const sendReply = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            await axios.post(`${API}/user/messages/reply`, { message: reply.trim(), in_reply_to: n.id }, { withCredentials: true });
            setSent(true);
            setReply('');
            setReplyOpen(false);
            toast.success('Reply sent — an admin will see it shortly');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    const canReply = n.type === 'admin_message';

    return (
        <div
            className={`px-4 py-3 border-b border-white/5 transition-colors ${!n.read ? 'bg-white/[0.03]' : ''}`}
            data-testid={`notification-${n.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0">
                    {typeIcons[n.type] || typeIcons.info}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm font-medium ${!n.read ? 'text-white' : 'text-[#A0AAB2]'}`}>
                            {n.title}
                        </p>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#39FF14] shrink-0" />}
                    </div>
                    <p className="text-xs text-[#A0AAB2] mt-0.5 break-words">{n.message}</p>
                    <div className="flex items-center justify-between gap-2 mt-1">
                        <p className="text-[10px] text-[#666]">{timeAgo(n.created_at)}</p>
                        {canReply && !sent && !replyOpen && (
                            <button
                                type="button"
                                onClick={() => setReplyOpen(true)}
                                className="text-[10px] text-[#FFD700] hover:underline font-semibold"
                                data-testid={`reply-btn-${n.id}`}
                            >
                                Reply →
                            </button>
                        )}
                        {sent && (
                            <span className="text-[10px] text-[#39FF14] font-semibold">✓ Reply sent</span>
                        )}
                    </div>

                    {replyOpen && (
                        <div className="mt-2 flex gap-2" data-testid={`reply-composer-${n.id}`}>
                            <input
                                type="text"
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                                maxLength={500}
                                placeholder="Type your reply…"
                                autoFocus
                                className="flex-1 bg-black/40 border border-[#FFD700]/30 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#FFD700]"
                            />
                            <button
                                type="button"
                                onClick={sendReply}
                                disabled={sending || !reply.trim()}
                                className="px-2 py-1 rounded bg-[#FFD700] text-black text-xs font-bold disabled:opacity-50"
                            >
                                <Send className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
